import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/**
 * Карта города с заведениями (п. 3, 7 ТЗ).
 *
 * Настоящая, а не картинка: гость решает по ней, далеко ли идти, и смотрит
 * на неё стоя на улице.
 *
 * Файл есть в обеих сборках — витрине и панели. Они собираются отдельно и
 * не делят зависимости: общий React из корня попал бы в бандл вторым
 * экземпляром, а с двумя React'ами не работают хуки. Стили карты при этом
 * общие (`design/map.css`) — их разделить можно, и они разделены.
 *
 * Настройки берутся запросом напрямую, а не через API-модуль приложения: их
 * адрес открыт и одинаков в обеих сборках.
 *
 * Метки рисуются своим HTML, а не стандартными булавками Leaflet. Булавка
 * тянет за собой картинку из пакета, которую сборщик кладёт по собственному
 * адресу, и на проде она обычно не находится; к тому же буква заведения
 * должна быть видна прямо на метке, а не во всплывающем окне.
 */

export interface MapPin {
  id: string;
  lat: number;
  lng: number;
  /** Что написано на метке: первая буква заведения. */
  label: string;
  title: string;
  kind: "point" | "you";
}

interface MapConfig {
  tileUrl: string;
  attribution: string;
  maxZoom: number;
}

/** Настройки карты запрашиваются один раз на страницу, а не на каждую карту. */
let configPromise: Promise<MapConfig> | null = null;

function mapConfig(): Promise<MapConfig> {
  configPromise ??= fetch("/api/public/map").then(response => {
    if (!response.ok) throw new Error("Настройки карты недоступны");
    return response.json() as Promise<MapConfig>;
  });

  return configPromise;
}

export function CityMap({
  center,
  pins,
  you,
  height = 260,
  onPick,
  onPlace
}: {
  center: { lat: number; lng: number };
  pins: MapPin[];
  /** Где стоит гость. Рисуется отдельной меткой, если он дал геолокацию. */
  you?: { lat: number; lng: number } | null;
  height?: number;
  /** Клик по метке: гость открывает меню заведения прямо с карты. */
  onPick?: (id: string) => void;
  /**
   * Клик по карте: партнёр ставит заведение туда, где оно стоит.
   *
   * Пока задан, вид не подгоняется под метки: карта перескакивала бы под
   * рукой после каждого клика.
   */
  onPlace?: (lat: number, lng: number) => void;
}) {
  const box = useRef<HTMLDivElement | null>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  const [failed, setFailed] = useState(false);
  /*
   * Карта создаётся после ответа за настройками, то есть позже первого
   * отрисовывания. Без этого признака метки рисовались бы один раз — в момент,
   * когда карты ещё нет, — и на неё уже не попадали бы.
   */
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!box.current || map.current) return;

    let cancelled = false;

    void mapConfig()
      .then(config => {
        if (cancelled || !box.current) return;

        const created = L.map(box.current, {
          center: [center.lat, center.lng],
          zoom: 14,
          // Прокрутка колесом внутри страницы уводит карту вместо страницы,
          // и на телефоне это единственный способ пролистать список дальше.
          scrollWheelZoom: false,
          attributionControl: true
        });

        L.tileLayer(config.tileUrl, {
          maxZoom: config.maxZoom,
          attribution: config.attribution
        }).addTo(created);

        layer.current = L.layerGroup().addTo(created);
        map.current = created;
        setReady(true);
      })
      .catch(() => !cancelled && setFailed(true));

    return () => {
      cancelled = true;
      map.current?.remove();
      map.current = null;
      layer.current = null;
      setReady(false);
    };
    // Центр меняется вместе с городом, а город за жизнь компонента не меняется.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const created = map.current;
    if (!created || !onPlace) return;

    const handler = (event: L.LeafletMouseEvent) => onPlace(event.latlng.lat, event.latlng.lng);

    created.on("click", handler);
    return () => {
      created.off("click", handler);
    };
  }, [onPlace, ready]);

  useEffect(() => {
    const group = layer.current;
    const created = map.current;
    if (!group || !created) return;

    group.clearLayers();

    const all = you
      ? [...pins, { id: "you", lat: you.lat, lng: you.lng, label: "•", title: "Вы здесь", kind: "you" as const }]
      : pins;

    for (const pin of all) {
      const marker = L.marker([pin.lat, pin.lng], {
        title: pin.title,
        icon: L.divIcon({
          className: "",
          html: `<span class="pin pin-${pin.kind}">${escapeHtml(pin.label)}</span>`,
          iconSize: [34, 34],
          iconAnchor: [17, 17]
        })
      });

      marker.bindTooltip(pin.title, { direction: "top", offset: [0, -18] });
      // Метка гостя никуда не ведёт: он на ней и так стоит.
      if (onPick && pin.kind === "point") marker.on("click", () => onPick(pin.id));

      marker.addTo(group);
    }

    /*
     * Вид подгоняется под метки, а не остаётся на фиксированном приближении.
     *
     * Заведения в городе стоят и в тридцати метрах друг от друга, и в пяти
     * километрах, и одно приближение показало бы либо пустое поле, либо
     * половину меток за краем экрана.
     */
    if (onPlace) return;

    if (all.length > 1) {
      created.fitBounds(L.latLngBounds(all.map(pin => [pin.lat, pin.lng])), {
        padding: [36, 36],
        maxZoom: 18
      });
    } else if (all.length === 1) {
      created.setView([all[0]!.lat, all[0]!.lng], 16);
    }
  }, [pins, you, onPick, onPlace, ready]);

  if (failed) {
    // Карта — не то, ради чего стоит ломать экран: список заведений работает
    // и без неё.
    return null;
  }

  return <div ref={box} className="map" style={{ height }} />;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"]/g, character => {
    switch (character) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      default:
        return "&quot;";
    }
  });
}
